import { useCallback, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { AccrualExtractionLineItem } from "@common/dto/financialDocument/accrual-document-extraction.schema";
import { BillExtractionRecord } from "@common/schemas/financialDocument/bill-extraction-record.schema";
import { InvoiceExtractionRecord } from "@common/schemas/financialDocument/invoice-extraction-record.schema";
import {
    batchUpdateBillExtractionRows,
    batchUpdateInvoiceExtractionRows,
    updateBillExtractionDetails,
    updateBillExtractions,
    updateInvoiceExtractionDetails,
    updateInvoiceExtractions,
} from "@api/financialDocument.api";
import { AccrualImporterRow } from "@components/accrualImporter/types";
import {
    groupRowsByExtractionId,
    rowsToBillExtractions,
    rowsToInvoiceExtractions,
} from "@components/accrualImporter/accrualExtraction.adapter";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";
import {
    ACCRUAL_CONTENT_ROW_LIMIT_MESSAGE,
    DetailsSnapshot,
    buildContentRowSnapshots,
    buildDetailsPatch,
    isDetailsEqual,
    isRowEqual,
    RowSnapshot,
    toDetailsSnapshot,
} from "./accrualDocumentRowSync.helpers";

interface ExtractionSnapshotState {
    rows: Map<number, RowSnapshot>;
    details: DetailsSnapshot | null;
}

interface UseAccrualDocumentRowSyncParams {
    financialDocumentId: number | null;
    mode: AccrualDocumentImporterMode;
    counterpartiesMap: Map<string, string>;
    onSyncError?: (error: Error) => void;
    onSyncSuccess?: () => void;
    onSyncWarning?: (message: string) => void;
}

function getExtractionIdSet(rows: AccrualImporterRow[]): Set<string> {
    return new Set(rows.map((row) => row.extractionId));
}

function hasStructuralExtractionChange({
    currentExtractionIds,
    persistedExtractionIds,
}: {
    currentExtractionIds: Set<string>;
    persistedExtractionIds: Set<string>;
}): boolean {
    if (currentExtractionIds.size !== persistedExtractionIds.size) {
        return true;
    }

    for (const extractionId of currentExtractionIds) {
        if (!persistedExtractionIds.has(extractionId)) {
            return true;
        }
    }

    return false;
}

export function useAccrualDocumentRowSync({
    financialDocumentId,
    mode,
    counterpartiesMap,
    onSyncError,
    onSyncSuccess,
    onSyncWarning,
}: UseAccrualDocumentRowSyncParams) {
    const snapshotsByExtractionId = useRef<
        Map<string, ExtractionSnapshotState>
    >(new Map());
    const persistedExtractionIds = useRef<Set<string>>(new Set());
    const isSavingRef = useRef(false);
    const queuedRowsRef = useRef<AccrualImporterRow[] | null>(null);
    const hasWarnedRowLimitRef = useRef(false);
    const existingExtractionsRef = useRef<
        InvoiceExtractionRecord[] | BillExtractionRecord[]
    >([]);
    const isBill = mode === "bills";

    const { mutateAsync: batchUpdateInvoiceRows } = useMutation({
        mutationFn: batchUpdateInvoiceExtractionRows,
    });

    const { mutateAsync: batchUpdateBillRows } = useMutation({
        mutationFn: batchUpdateBillExtractionRows,
    });

    const { mutateAsync: updateInvoiceDetails } = useMutation({
        mutationFn: updateInvoiceExtractionDetails,
    });

    const { mutateAsync: updateBillDetails } = useMutation({
        mutationFn: updateBillExtractionDetails,
    });

    const { mutateAsync: replaceInvoiceExtractions } = useMutation({
        mutationFn: updateInvoiceExtractions,
    });

    const { mutateAsync: replaceBillExtractions } = useMutation({
        mutationFn: updateBillExtractions,
    });

    const warnIfRowsTruncated = useCallback(
        (hasTruncatedRows: boolean) => {
            if (!hasTruncatedRows || hasWarnedRowLimitRef.current) {
                return;
            }

            hasWarnedRowLimitRef.current = true;
            onSyncWarning?.(ACCRUAL_CONTENT_ROW_LIMIT_MESSAGE);
        },
        [onSyncWarning],
    );

    const updateSnapshotsForGroup = useCallback(
        (
            extractionId: string,
            groupRows: AccrualImporterRow[],
            options?: { shouldWarnTruncation?: boolean },
        ) => {
            const { snapshots, hasTruncatedRows } =
                buildContentRowSnapshots(groupRows);
            if (options?.shouldWarnTruncation !== false) {
                warnIfRowsTruncated(hasTruncatedRows);
            }
            snapshotsByExtractionId.current.set(extractionId, {
                rows: snapshots,
                details: toDetailsSnapshot({
                    firstRow: groupRows[0],
                    counterpartiesMap,
                }),
            });
        },
        [counterpartiesMap, warnIfRowsTruncated],
    );

    const persistAllExtractions = useCallback(
        async (rows: AccrualImporterRow[]) => {
            if (!financialDocumentId) {
                return;
            }

            if (isBill) {
                const billExtractions = rowsToBillExtractions({
                    rows,
                    counterpartiesMap,
                    existingExtractions: existingExtractionsRef.current,
                });
                await replaceBillExtractions({
                    financialDocumentId,
                    billExtractions,
                });
                existingExtractionsRef.current = billExtractions;
            } else {
                const invoiceExtractions = rowsToInvoiceExtractions({
                    rows,
                    counterpartiesMap,
                    existingExtractions: existingExtractionsRef.current,
                });
                await replaceInvoiceExtractions({
                    financialDocumentId,
                    invoiceExtractions,
                });
                existingExtractionsRef.current = invoiceExtractions;
            }

            persistedExtractionIds.current = getExtractionIdSet(rows);
            snapshotsByExtractionId.current.clear();
            for (const [extractionId, groupRows] of groupRowsByExtractionId(
                rows,
            )) {
                updateSnapshotsForGroup(extractionId, groupRows);
            }
            onSyncSuccess?.();
        },
        [
            counterpartiesMap,
            financialDocumentId,
            isBill,
            onSyncSuccess,
            replaceBillExtractions,
            replaceInvoiceExtractions,
            updateSnapshotsForGroup,
        ],
    );

    const persistIncrementalGroup = useCallback(
        async ({
            extractionId,
            groupRows,
        }: {
            extractionId: string;
            groupRows: AccrualImporterRow[];
        }) => {
            if (!financialDocumentId) {
                return;
            }

            const previousState = snapshotsByExtractionId.current.get(
                extractionId,
            ) ?? {
                rows: new Map<number, RowSnapshot>(),
                details: null,
            };
            const previous = previousState.rows;
            const { snapshots: next, hasTruncatedRows } =
                buildContentRowSnapshots(groupRows);
            warnIfRowsTruncated(hasTruncatedRows);

            const upserts: Array<
                AccrualExtractionLineItem & { rowIndex: number }
            > = [];
            const deletes: number[] = [];

            for (const [rowIndex, snapshot] of next) {
                const prev = previous.get(rowIndex);
                if (prev && isRowEqual(prev, snapshot)) {
                    continue;
                }

                upserts.push({ rowIndex, ...snapshot });
            }

            for (const rowIndex of previous.keys()) {
                if (next.has(rowIndex)) {
                    continue;
                }

                deletes.push(rowIndex);
            }

            const nextDetails = toDetailsSnapshot({
                firstRow: groupRows[0],
                counterpartiesMap,
            });
            const previousDetails = previousState.details;
            const hasDetailsChange =
                !previousDetails ||
                !isDetailsEqual(previousDetails, nextDetails);
            const detailsPatch = hasDetailsChange
                ? buildDetailsPatch({
                      previous: previousDetails,
                      next: nextDetails,
                      firstRow: groupRows[0],
                  })
                : {};
            const hasDetailsPatch = Object.keys(detailsPatch).length > 0;
            const hasRowChanges = upserts.length > 0 || deletes.length > 0;

            if (!hasRowChanges && !hasDetailsPatch) {
                return;
            }

            if (hasRowChanges) {
                if (isBill) {
                    await batchUpdateBillRows({
                        financialDocumentId,
                        billExtractionId: extractionId,
                        upserts,
                        deletes,
                    });
                } else {
                    await batchUpdateInvoiceRows({
                        financialDocumentId,
                        invoiceExtractionId: extractionId,
                        upserts,
                        deletes,
                    });
                }
            }

            if (hasDetailsPatch) {
                if (isBill) {
                    await updateBillDetails({
                        financialDocumentId,
                        billExtractionId: extractionId,
                        ...detailsPatch,
                    });
                } else {
                    await updateInvoiceDetails({
                        financialDocumentId,
                        invoiceExtractionId: extractionId,
                        ...detailsPatch,
                    });
                }
            }

            updateSnapshotsForGroup(extractionId, groupRows);
        },
        [
            batchUpdateBillRows,
            batchUpdateInvoiceRows,
            counterpartiesMap,
            financialDocumentId,
            isBill,
            updateBillDetails,
            updateInvoiceDetails,
            updateSnapshotsForGroup,
            warnIfRowsTruncated,
        ],
    );

    const persistRows = useCallback(
        async (rows: AccrualImporterRow[]) => {
            if (!financialDocumentId || rows.length === 0) {
                return;
            }

            const groups = groupRowsByExtractionId(rows);
            const currentExtractionIds = getExtractionIdSet(rows);
            const hasStructuralChange = hasStructuralExtractionChange({
                currentExtractionIds,
                persistedExtractionIds: persistedExtractionIds.current,
            });

            if (
                hasStructuralChange ||
                persistedExtractionIds.current.size === 0
            ) {
                await persistAllExtractions(rows);
                return;
            }

            for (const [extractionId, groupRows] of groups) {
                await persistIncrementalGroup({ extractionId, groupRows });
            }

            onSyncSuccess?.();
        },
        [
            financialDocumentId,
            onSyncSuccess,
            persistAllExtractions,
            persistIncrementalGroup,
        ],
    );

    const drainSaveQueue = useCallback(async () => {
        isSavingRef.current = true;
        const drain = async () => {
            while (queuedRowsRef.current) {
                const rowsToSave = queuedRowsRef.current;
                queuedRowsRef.current = null;
                try {
                    await persistRows(rowsToSave);
                } catch (error) {
                    onSyncError?.(
                        error instanceof Error
                            ? error
                            : new Error("Failed to save accrual document"),
                    );
                }
            }
        };
        await drain().finally(() => {
            isSavingRef.current = false;
        });
    }, [onSyncError, persistRows]);

    const afterChange = useCallback(
        (rows: AccrualImporterRow[]) => {
            if (!financialDocumentId) {
                return;
            }

            queuedRowsRef.current = rows;
            if (isSavingRef.current) {
                return;
            }

            void drainSaveQueue();
        },
        [drainSaveQueue, financialDocumentId],
    );

    const initializeSnapshot = useCallback(
        (
            initialRows: AccrualImporterRow[],
            persistedIds: string[] = [],
            existingExtractions:
                | InvoiceExtractionRecord[]
                | BillExtractionRecord[] = [],
        ) => {
            queuedRowsRef.current = null;
            isSavingRef.current = false;
            hasWarnedRowLimitRef.current = false;
            persistedExtractionIds.current = new Set(persistedIds);
            existingExtractionsRef.current = existingExtractions;
            snapshotsByExtractionId.current.clear();

            for (const [extractionId, groupRows] of groupRowsByExtractionId(
                initialRows,
            )) {
                updateSnapshotsForGroup(extractionId, groupRows, {
                    shouldWarnTruncation: false,
                });
            }
        },
        [updateSnapshotsForGroup],
    );

    const syncServerCounterpartyAssignments = useCallback(
        (
            extractions: InvoiceExtractionRecord[] | BillExtractionRecord[],
            patchedRows: AccrualImporterRow[],
        ) => {
            existingExtractionsRef.current = extractions;

            for (const [extractionId, groupRows] of groupRowsByExtractionId(
                patchedRows,
            )) {
                const extraction = extractions.find(
                    (candidate) => candidate.id === extractionId,
                );
                if (!extraction?.counterpartyId) {
                    continue;
                }

                const previousState =
                    snapshotsByExtractionId.current.get(extractionId);
                if (!previousState) {
                    continue;
                }

                const nextCounterpartyDetails = toDetailsSnapshot({
                    firstRow: groupRows[0],
                    counterpartiesMap,
                });

                snapshotsByExtractionId.current.set(extractionId, {
                    rows: previousState.rows,
                    details: previousState.details
                        ? {
                              ...previousState.details,
                              counterpartyId:
                                  nextCounterpartyDetails.counterpartyId,
                              counterpartyName:
                                  nextCounterpartyDetails.counterpartyName,
                          }
                        : nextCounterpartyDetails,
                });
            }
        },
        [counterpartiesMap],
    );

    return {
        afterChange,
        initializeSnapshot,
        syncServerCounterpartyAssignments,
    };
}
